// ─────────────────────────────────────────────
// KLOTHIQ — Common Zod Schemas
// ─────────────────────────────────────────────

import { z } from 'zod';

/** UUID format */
export const uuidSchema = z.string().uuid('Invalid ID format');

/** Money: numeric string, non-negative, up to 2 decimal places */
export const moneySchema = z
  .string()
  .regex(/^\d+(\.\d{1,2})?$/, 'Must be a valid amount (e.g. 1234.50)')
  .refine((val) => parseFloat(val) >= 0, 'Amount cannot be negative');

/** Money that can be zero */
export const moneyOrZeroSchema = z
  .string()
  .regex(/^\d+(\.\d{1,2})?$/, 'Must be a valid amount')
  .default('0.00');

/** Positive integer quantity */
export const quantitySchema = z
  .number()
  .int('Quantity must be a whole number')
  .positive('Quantity must be greater than zero');

/** Non-negative integer */
export const nonNegativeIntSchema = z
  .number()
  .int('Must be a whole number')
  .min(0, 'Cannot be negative');

/** ISO date string (YYYY-MM-DD) */
export const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be a valid date (YYYY-MM-DD)');

/** Pagination params */
export const paginationSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
});

/** Search/filter params */
export const searchSchema = z.object({
  search: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

/** Phone number — optional, Indian format */
export const phoneSchema = z
  .string()
  .regex(/^[6-9]\d{9}$/, 'Must be a valid 10-digit Indian mobile number')
  .nullable()
  .optional();

/** Customer contact may be blank; WhatsApp validates the recipient separately. */
export const optionalCustomerPhoneSchema = z
  .string()
  .trim()
  .max(20, 'Phone too long')
  .refine(
    (value) =>
      !value ||
      (/^[+\d\s\-()]+$/.test(value) &&
        value.replace(/\D/g, '').length >= 10 &&
        value.replace(/\D/g, '').length <= 15),
    'Enter a valid phone number',
  )
  .nullable()
  .optional();

/** Email — optional */
export const emailSchema = z.string().email('Invalid email address').nullable().optional();

/** Pincode — optional, 6 digits */
export const pincodeSchema = z
  .string()
  .regex(/^\d{6}$/, 'Must be a 6-digit pincode')
  .nullable()
  .optional();
